import { PLAYER_RADIUS } from './constants';
import { castRay, circleFits, isSolid, lineOfSight, tileCenter } from './tilemap';
import type { Circle, Pillar, TileMap, Vec2 } from './types';
import { rayCircle } from './vec';

// Pillars: immovable round obstacles on top of the tile grid. These helpers answer the questions
// tilemap.ts answers for walls, for walls and pillars together.

/** The first pillar a circle at (x, y) overlaps, or null. */
export function pillarAt(pillars: readonly Pillar[], x: number, y: number, r: number): Pillar | null {
  for (const p of pillars) {
    const dx = x - p.pos.x;
    const dy = y - p.pos.y;
    const rsum = r + p.radius;
    if (dx * dx + dy * dy < rsum * rsum) return p;
  }
  return null;
}

/** Is a circle free of walls and pillars here? */
export function isFree(map: TileMap, pillars: readonly Pillar[], x: number, y: number, r: number): boolean {
  return circleFits(map, x, y, r) && pillarAt(pillars, x, y, r) === null;
}

/** Does the segment a–b pass through the circle? An endpoint inside counts as a hit. */
export function segmentHitsCircle(a: Vec2, b: Vec2, c: Circle): boolean {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const acx = c.pos.x - a.x;
  const acy = c.pos.y - a.y;
  const ab2 = abx * abx + aby * aby;
  const t = ab2 > 0 ? Math.max(0, Math.min(1, (acx * abx + acy * aby) / ab2)) : 0;
  const px = a.x + abx * t - c.pos.x;
  const py = a.y + aby * t - c.pos.y;
  return px * px + py * py < c.radius * c.radius;
}

/** Line of sight past walls and pillars. */
export function hasLineOfSight(map: TileMap, pillars: readonly Pillar[], a: Vec2, b: Vec2): boolean {
  if (!lineOfSight(map, a, b)) return false;
  for (const p of pillars) if (segmentHitsCircle(a, b, p)) return false;
  return true;
}

/** Distance a ray travels from `from` along `dir` (unit) before it meets a wall or a pillar, capped at maxDist. */
export function castRayFree(map: TileMap, pillars: readonly Pillar[], from: Vec2, dir: Vec2, maxDist: number): number {
  let length = castRay(map, from, dir, maxDist);
  for (const p of pillars) {
    const hit = rayCircle(from, dir, p.pos, p.radius);
    if (hit !== null && hit < length) length = hit;
  }
  return length;
}

/**
 * Can a player stand at the centre of this tile? Walls block, and so does a pillar a player there
 * would touch. The grid-based path finding (bot, level check) walks over passable tiles.
 */
export function tilePassable(map: TileMap, pillars: readonly Pillar[], tx: number, ty: number): boolean {
  if (isSolid(map, tx, ty)) return false;
  const c = tileCenter(tx, ty);
  return pillarAt(pillars, c.x, c.y, PLAYER_RADIUS) === null;
}
